/**
 * Utility functions for URL generation and manipulation
 */

/**
 * Generate a clean URL for work filtering
 * @param tags - Array of tag slugs
 * @param categories - Array of category slugs
 * @param sortingType - Sorting type (optional)
 * @param baseUrl - Base URL (default: '/work')
 * @returns Clean URL with query parameters
 */
export const generateWorkFilterUrl = (
  tags: string[] | string = [],
  categories: string[] = [],
  sortingType?: string,
  baseUrl = "/work"
): string => {
  // Handle legacy single parameter (array of mixed tags/categories)
  if (
    typeof tags === "string" ||
    (Array.isArray(tags) &&
      categories.length === 0 &&
      tags.some((t) => t.startsWith("/")))
  ) {
    const allFilters = Array.isArray(tags) ? tags : [tags]
    const separatedTags = allFilters.filter((t) => !t.startsWith("/"))
    const separatedCategories = allFilters
      .filter((t) => t.startsWith("/"))
      .map((c) => c.substring(1))

    const params = new URLSearchParams()
    if (separatedTags.length > 0) {
      params.set("tags", separatedTags.join(","))
    }
    if (separatedCategories.length > 0) {
      params.set("categories", separatedCategories.join(","))
    }
    if (sortingType && sortingType !== "recent") {
      params.set("sort", sortingType)
    }

    return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl
  }

  // New separate parameters approach
  const params = new URLSearchParams()

  if (Array.isArray(tags) && tags.length > 0) {
    params.set("tags", tags.join(","))
  }

  if (categories.length > 0) {
    params.set("categories", categories.join(","))
  }

  if (sortingType && sortingType !== "recent") {
    params.set("sort", sortingType)
  }

  return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl
}

/**
 * Parse tags from URL search params
 * @param searchParams - URLSearchParams object
 * @returns Array of tag slugs
 */
export const parseTagsFromUrl = (searchParams: URLSearchParams): string[] => {
  const tagsParam = searchParams.get("tags")
  return tagsParam ? tagsParam.split(",").filter(Boolean) : []
}

/**
 * Generate a shareable URL for the current page
 * @param tags - Current selected tags
 * @param origin - Window origin (e.g., 'https://example.com')
 * @returns Full shareable URL
 */
export const generateShareableUrl = (
  tags: string[],
  origin: string
): string => {
  const baseUrl = `${origin}/work`
  return generateWorkFilterUrl(tags, [], undefined, baseUrl)
}

/**
 * Create SEO-friendly page title based on selected tags
 * @param tags - Array of tag objects with name/title
 * @param siteName - Site name to append
 * @returns SEO-friendly page title
 */
export const generatePageTitle = (
  tags: Array<{ name?: string; title?: string }>,
  siteName = "Your Site Name"
): string => {
  if (tags.length === 0) {
    return `Work | ${siteName}`
  }

  const tagNames = tags.map((tag) => tag.name || tag.title).join(", ")
  return `Work - ${tagNames} | ${siteName}`
}

/**
 * Create SEO-friendly meta description based on selected tags
 * @param tags - Array of tag objects with name/title
 * @returns SEO-friendly meta description
 */
export const generateMetaDescription = (
  tags: Array<{ name?: string; title?: string }>
): string => {
  if (tags.length === 0) {
    return "Browse our latest articles, tutorials, and insights on web development, programming, and technology."
  }

  const tagNames = tags.map((tag) => tag.name || tag.title).join(", ")
  return `Browse articles about ${tagNames}. Find tutorials, insights, and tips on ${tagNames}.`
}
